// ===================================================================
// services/challengeService.ts
// Gestion des défis en ligne entre amis (Supabase).
//
// SQL requis (à exécuter dans Supabase SQL Editor — SANS les //) :
//
// CREATE TABLE challenges (
//   id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
//   challenger_id UUID REFERENCES user_profiles(id) ON DELETE CASCADE,
//   challenged_id UUID REFERENCES user_profiles(id) ON DELETE CASCADE,
//   language_code TEXT NOT NULL,
//   game_mode TEXT NOT NULL DEFAULT 'quiz',
//   questions JSONB NOT NULL,
//   challenger_score INTEGER NOT NULL,
//   challenged_score INTEGER,
//   status TEXT NOT NULL DEFAULT 'pending',
//   created_at TIMESTAMPTZ DEFAULT NOW(),
//   expires_at TIMESTAMPTZ DEFAULT (NOW() + INTERVAL '24 hours')
// );
// ALTER TABLE challenges ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "read_own" ON challenges FOR SELECT
//   USING (auth.uid() = challenger_id OR auth.uid() = challenged_id);
// CREATE POLICY "insert_challenge" ON challenges FOR INSERT
//   WITH CHECK (auth.uid() = challenger_id);
// CREATE POLICY "update_challenged" ON challenges FOR UPDATE
//   USING (auth.uid() = challenged_id);
// ===================================================================
import { supabase } from './supabaseAudioService';
import { LanguageCode } from '../types/types';
import secureAuthService from './secureAuthService';

export type GameMode = 'quiz' | 'cultural';

export type ChallengeTheme  = 'Histoire' | 'Géographie' | 'Politique' | 'Spiritualité' | 'mix';
export type ChallengeFormat = 'eclair' | 'classique' | 'marathon';

// Réglages du défi (Arène). Stockés dans questions[0].meta : pas de colonne
// dédiée, le schéma SQL ci-dessus reste inchangé.
export interface ChallengeMeta {
  theme: ChallengeTheme;
  format: ChallengeFormat;
  stake: number; // XP remportés par le gagnant (0 = sans mise)
}

export interface ChallengeQuestion {
  word: string;
  translation: string;
  choices: string[];
  correct: string;
  // Réponse du challenger (null = temps écoulé) — affichée à l'adversaire
  challenger_answer?: string | null;
  meta?: ChallengeMeta;
}

export const FORMAT_SPECS: Record<ChallengeFormat, { questions: number; seconds: number }> = {
  eclair:    { questions: 5,  seconds: 30 }, // 5 Q · 2,5 min
  classique: { questions: 10, seconds: 30 }, // 10 Q · 5 min
  marathon:  { questions: 20, seconds: 30 }, // 20 Q · 10 min
};

// Anciens défis sans meta : format déduit du nombre de questions
export const getChallengeMeta = (questions: ChallengeQuestion[]): ChallengeMeta => {
  const m = questions[0]?.meta;
  if (m) return m;
  const n = questions.length;
  return { theme: 'mix', format: n <= 5 ? 'eclair' : n >= 20 ? 'marathon' : 'classique', stake: 0 };
};

export interface Challenge {
  id: string;
  challenger_id: string;
  challenged_id: string;
  language_code: LanguageCode;
  game_mode: GameMode;
  questions: ChallengeQuestion[];
  challenger_score: number;
  challenged_score: number | null;
  status: 'pending' | 'completed' | 'declined';
  created_at: string;
  expires_at: string;
  // Enrichi côté client
  challenger_username?: string | null;
  challenged_username?: string | null;
}

// Crée un défi et le stocke dans Supabase
export const createChallenge = async (
  challengerId: string,
  challengedId: string,
  languageCode: LanguageCode,
  gameMode: GameMode,
  questions: ChallengeQuestion[],
  challengerScore: number
): Promise<string> => {
  const { data, error } = await supabase
    .from('challenges')
    .insert({
      challenger_id: challengerId,
      challenged_id: challengedId,
      language_code: languageCode,
      game_mode: gameMode,
      questions,
      challenger_score: challengerScore,
      status: 'pending',
      expires_at: new Date(Date.now() + 48 * 3600 * 1000).toISOString(),
    })
    .select('id')
    .single();

  if (error) throw error;

  // Notification push (Android/FCM) à l'adversaire — best effort, non bloquant :
  // le défi est déjà créé, et le toast temps réel prend le relais si l'app est ouverte.
  secureAuthService
    .fetchWithAnyAuth('/api/push/challenge', {
      method: 'POST',
      body: JSON.stringify({ challengeId: data.id }),
    })
    .catch(() => {});

  return data.id as string;
};

// Récupère les défis en attente reçus par l'utilisateur
export const getPendingChallenges = async (userId: string): Promise<Challenge[]> => {
  const { data, error } = await supabase
    .from('challenges')
    .select('*')
    .eq('challenged_id', userId)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) throw error;
  return enrichWithUsernames((data ?? []) as Challenge[]);
};

// Récupère toutes les parties de l'utilisateur (pending + completed) des 7 derniers jours
export const getMatchHistory = async (userId: string): Promise<Challenge[]> => {
  const weekAgo = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
  const { data, error } = await supabase
    .from('challenges')
    .select('*')
    .gt('created_at', weekAgo)
    .order('created_at', { ascending: false })
    .limit(30);

  if (error) throw error;
  return enrichWithUsernames((data ?? []) as Challenge[], true);
};

// Termine un défi reçu avec le score du challengé
export const completeChallenge = async (
  challengeId: string,
  challengedScore: number
): Promise<void> => {
  const { error } = await supabase
    .from('challenges')
    .update({ challenged_score: challengedScore, status: 'completed' })
    .eq('id', challengeId);

  if (error) throw error;
};

// Refuse un défi reçu (autorisé par la policy "update_challenged")
export const declineChallenge = async (challengeId: string): Promise<void> => {
  const { error } = await supabase
    .from('challenges')
    .update({ status: 'declined' })
    .eq('id', challengeId);

  if (error) throw error;
};

// ── Utilitaire interne : enrichit les challenges avec les noms ──
async function enrichWithUsernames(
  challenges: Challenge[],
  includeChallenged = false
): Promise<Challenge[]> {
  if (challenges.length === 0) return challenges;

  const ids = [...new Set([
    ...challenges.map(c => c.challenger_id),
    ...(includeChallenged ? challenges.map(c => c.challenged_id) : []),
  ])];

  const { data: profiles } = await supabase
    .from('user_profiles')
    .select('id, username, display_name')
    .in('id', ids);

  const map = new Map((profiles ?? []).map((p: any) => [p.id, p]));

  challenges.forEach(c => {
    const cr = map.get(c.challenger_id) as any;
    c.challenger_username = cr?.display_name || cr?.username || 'Joueur';
    if (includeChallenged) {
      const cd = map.get(c.challenged_id) as any;
      c.challenged_username = cd?.display_name || cd?.username || 'Joueur';
    }
  });

  return challenges;
}
